import * as React from "react";

import { useListControlContext as useVendorListControlContext } from "@salt-ds/core";

import { useControlled } from "./useControlled";

/**
 * The shared state behind Dropdown and ComboBox: which options exist, which is active, which are
 * selected, and how the keyboard moves between them.
 *
 * ── OPTIONS REGISTER THEMSELVES, AND ARE SORTED BY DOM POSITION ───────────────────────
 *
 * A list cannot know its options from props: they are children, they may be nested inside
 * groups, and they can appear and disappear as a filter narrows. So each option registers on
 * mount and the list sorts them with `compareDocumentPosition`.
 *
 * Registration ORDER is not reading order — React mounts children in whatever order it commits,
 * and a re-render can register an option that belongs in the middle. Sorting by document
 * position is what makes ArrowDown go to the option the user can SEE below, rather than to
 * whichever one happened to mount next.
 *
 * A `MutationObserver` re-sorts when options are added, because a filtered list rewrites its
 * children without those options remounting in order.
 *
 * ── TYPEAHEAD USES A COLLATOR, NOT `startsWith` ───────────────────────────────────────
 *
 * `Intl.Collator` with `sensitivity: "base"` makes "e" match "Édouard" and "É" match "edouard".
 * A `toLowerCase().startsWith()` version fails on every accented name in the list, which for a
 * supplier directory is most of the interesting ones.
 *
 * Typing the SAME letter repeatedly cycles through options beginning with it — the behaviour
 * every native select has — which is why the all-same-letter case is handled separately.
 */
export interface OptionValue<Item = string> {
  id: string;
  disabled: boolean;
  value: Item;
}

interface RegisteredOption<Item> {
  data: OptionValue<Item>;
  element: HTMLElement;
}

/**
 * Why the list opened or closed, as reported to `onOpenChange`.
 *
 * These are the VENDOR's two values. An earlier version of this file had `"focus"` instead of
 * `"manual"`, which was wrong in both directions: nothing ever passed `"focus"`, and a caller
 * distinguishing a deliberate toggle from typing had no value to test for. Do not confuse this
 * with floating-ui's own `reason`, a larger set that DOES include `"focus"` and arrives on a
 * different callback.
 */
export type OpenChangeReason = "input" | "manual";

export interface ListControlProps<Item = string> {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean, reason?: OpenChangeReason) => void;
  multiselect?: boolean;
  selected?: Item[];
  defaultSelected?: Item[];
  onSelectionChange?: (event: React.SyntheticEvent, selected: Item[]) => void;
  disabled?: boolean;
  readOnly?: boolean;
  valueToString?: (item: Item) => string;
}

const defaultValueToString = (item: unknown) => String(item);

export function useListControl<Item = string>(props: ListControlProps<Item>) {
  const {
    defaultOpen,
    defaultSelected,
    disabled,
    multiselect,
    onOpenChange,
    onSelectionChange,
    open: openProp,
    readOnly,
    selected: selectedProp,
    valueToString = defaultValueToString as (item: Item) => string,
  } = props;

  const listRef = React.useRef<HTMLElement | null>(null);
  const [listElement, setListElement] = React.useState<HTMLElement | null>(null);
  const setListRef = React.useCallback((node: HTMLElement | null) => {
    listRef.current = node;
    setListElement(node);
  }, []);

  const [focusedState, setFocusedState] = React.useState(false);
  /*
   * Focus-VISIBLE is tracked separately from focus, and that distinction is the point: the
   * active option is highlighted only when the user is navigating by keyboard. Moving the mouse
   * over the list clears it, so a pointer user does not see a stray keyboard highlight following
   * them around.
   */
  const [focusVisibleState, setFocusVisibleState] = React.useState(false);

  React.useEffect(() => {
    if (disabled && focusedState) {
      setFocusedState(false);
      setFocusVisibleState(false);
    }
  }, [disabled, focusedState]);

  const [activeState, setActiveState] = React.useState<OptionValue<Item> | undefined>(undefined);
  const setActive = React.useCallback((option?: OptionValue<Item>) => {
    setActiveState(option ?? undefined);
  }, []);

  const [openState, setOpenState] = useControlled<boolean>({
    controlled: openProp,
    default: Boolean(defaultOpen),
  });

  /** Which key opened the list, so the first active option can be the first or last accordingly. */
  const openKey = React.useRef<string | undefined>(undefined);

  const setOpen = (newOpen: boolean, reason?: OpenChangeReason, key?: string) => {
    // A disabled or read-only list never opens, whatever asked it to.
    if (disabled || readOnly) return;
    setOpenState(newOpen);
    openKey.current = key;
    if (newOpen !== openState) onOpenChange?.(newOpen, reason);
  };

  const [selectedState, setSelectedState] = useControlled<Item[]>({
    controlled: selectedProp,
    default: defaultSelected ?? [],
  });

  const select = (event: React.SyntheticEvent, option: OptionValue<Item>) => {
    if (option.disabled || readOnly || disabled) return;
    let newSelected = [option.value];
    if (multiselect) {
      // Multi-select TOGGLES: clicking a selected option removes it, which is the only way to
      // deselect in a list with no checkboxes to uncheck.
      newSelected = selectedState.includes(option.value)
        ? selectedState.filter((item) => item !== option.value)
        : selectedState.concat([option.value]);
    }
    setSelectedState(newSelected);
    onSelectionChange?.(event, newSelected);
    // Single-select closes on choice; multi-select stays open so more can be picked.
    if (!multiselect) setOpen(false);
  };

  const clear = (event: React.SyntheticEvent) => {
    setSelectedState([]);
    // Only reported if something was actually cleared.
    if (selectedState.length !== 0) onSelectionChange?.(event, []);
  };

  const optionsRef = React.useRef<RegisteredOption<Item>[]>([]);

  const register = React.useCallback((optionValue: OptionValue<Item>, element: HTMLElement) => {
    const existingIndex = optionsRef.current.findIndex((item) => item.data.id === optionValue.id);
    if (existingIndex !== -1) optionsRef.current.splice(existingIndex, 1);
    optionsRef.current.push({ data: optionValue, element });
    return () => {
      optionsRef.current = optionsRef.current.filter((item) => item.data.id !== optionValue.id);
    };
  }, []);

  React.useEffect(() => {
    const sortOptions = () => {
      optionsRef.current = optionsRef.current
        // Disconnected elements are stale registrations from options that have gone.
        .filter((item) => item.element.isConnected)
        .sort(({ element: a }, { element: b }) => {
          if (a === b) return 0;
          const pos = a.compareDocumentPosition(b);
          if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
          if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
          return 0;
        });
    };

    if (!listElement) return;
    const observer = new MutationObserver((mutations) => {
      const optionsChanged = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) => node instanceof HTMLElement && node.matches?.('[role="option"]'),
        ),
      );
      if (optionsChanged) sortOptions();
    });
    observer.observe(listElement, { childList: true, subtree: true });
    sortOptions();
    return () => observer.disconnect();
  }, [listElement]);

  const getOptionAtIndex = React.useCallback((index: number) => optionsRef.current[index], []);
  const getIndexOfOption = React.useCallback(
    (option: OptionValue<Item>) =>
      optionsRef.current.findIndex((item) => item.data.value === option.value),
    [],
  );
  const getOptionsMatching = React.useCallback(
    (predicate: (option: OptionValue<Item>) => boolean) =>
      optionsRef.current.filter((item) => predicate(item.data)),
    [],
  );

  const getOptionFromSearch = React.useCallback(
    (search: string, startFrom?: OptionValue<Item>) => {
      // Accent- and case-insensitive: "e" finds "Édouard". `startsWith` would not.
      const collator = new Intl.Collator("en", { usage: "search", sensitivity: "base" });
      const startIndex = startFrom ? getIndexOfOption(startFrom) + 1 : 0;
      const searchList = optionsRef.current.map((item) => item.data);

      let matches = searchList.filter(
        (option) =>
          collator.compare(valueToString(option.value).substring(0, search.length), search) === 0,
      );

      if (matches.length === 0) {
        /*
         * Repeating one letter CYCLES through options starting with it — "a a a" walks Acme,
         * Apex, Avalon — which is what every native select does. Without this branch the second
         * "a" searches for "aa" and matches nothing.
         */
        const letters = search.split("");
        const allSameLetter =
          letters.length > 0 && letters.every((letter) => collator.compare(letter, letters[0]) === 0);
        if (allSameLetter) {
          matches = searchList.filter(
            (option) =>
              collator.compare(valueToString(option.value)[0].toLowerCase(), letters[0]) === 0,
          );
        }
      }

      // Wraps by searching from the option AFTER the current one.
      return matches.find((option) => getIndexOfOption(option) >= startIndex);
    },
    [getIndexOfOption, valueToString],
  );

  const getFirstOption = React.useCallback(() => getOptionAtIndex(0), [getOptionAtIndex]);
  const getLastOption = React.useCallback(
    () => getOptionAtIndex(optionsRef.current.length - 1),
    [getOptionAtIndex],
  );
  const getOptionBefore = React.useCallback(
    (option: OptionValue<Item>) => getOptionAtIndex(getIndexOfOption(option) - 1),
    [getIndexOfOption, getOptionAtIndex],
  );
  const getOptionAfter = React.useCallback(
    (option: OptionValue<Item>) => getOptionAtIndex(getIndexOfOption(option) + 1),
    [getIndexOfOption, getOptionAtIndex],
  );

  /*
   * Page Up and Page Down move by a VISIBLE page, measured from the rendered rectangles, not by
   * a fixed number of options. Options are not all the same height once any of them wraps to two
   * lines, so a fixed step would skip past the bottom of the viewport on some lists and stop
   * short on others.
   */
  const getOptionPageAbove = React.useCallback(
    (start: OptionValue<Item>) => {
      const list = listRef.current;
      let option = optionsRef.current.find((item) => item.data === start);
      if (!list || !option) return undefined;
      const containerRect = list.getBoundingClientRect();
      let optionRect = option.element.getBoundingClientRect();
      const listY = containerRect.y - list.scrollTop;
      const pageY = Math.max(0, optionRect.y - listY + optionRect.height - containerRect.height);
      while (option && optionRect && optionRect.y - listY > pageY) {
        option = getOptionBefore(option.data);
        optionRect = option?.element?.getBoundingClientRect() as DOMRect;
      }
      return option ?? getFirstOption();
    },
    [getFirstOption, getOptionBefore],
  );

  const getOptionPageBelow = React.useCallback(
    (start: OptionValue<Item>) => {
      const list = listRef.current;
      let option = optionsRef.current.find((item) => item.data === start);
      if (!list || !option) return undefined;
      const containerRect = list.getBoundingClientRect();
      let optionRect = option.element.getBoundingClientRect();
      const listY = containerRect.y - list.scrollTop;
      const pageY = Math.min(
        list.scrollHeight,
        optionRect.y - listY - optionRect.height + containerRect.height,
      );
      while (option && optionRect && optionRect.y - listY < pageY) {
        option = getOptionAfter(option.data);
        optionRect = option?.element?.getBoundingClientRect() as DOMRect;
      }
      return option ?? getLastOption();
    },
    [getLastOption, getOptionAfter],
  );

  return {
    activeState,
    setActive,
    openState,
    setOpen,
    openKey,
    selectedState,
    // Exposed for ComboBox, which owns a DIFFERENT selection rule: it also has to write the
    // input's text on selection, so it replaces `select` rather than calling it.
    setSelectedState,
    select,
    clear,
    register,
    getOptionAtIndex,
    getIndexOfOption,
    getOptionsMatching,
    getOptionFromSearch,
    getFirstOption,
    getLastOption,
    getOptionBefore,
    getOptionAfter,
    getOptionPageAbove,
    getOptionPageBelow,
    focusedState,
    setFocusedState,
    focusVisibleState,
    setFocusVisibleState,
    listRef,
    setListRef,
    multiselect,
    valueToString,
    disabled,
    readOnly,
  };
}

export type ListControl<Item = string> = ReturnType<typeof useListControl<Item>>;

export const ListControlContext = React.createContext<ListControl<never> | null>(null);

/**
 * Reads OUR list context, falling back to the VENDOR's.
 *
 * ── WHY AN OPTION HAS TO SPEAK BOTH ───────────────────────────────────────────────────
 *
 * `Dropdown` is ours and `ComboBox` is still Salt's, and both provide a list context to the same
 * `Option`. A context is keyed by object identity, so an Option that read only ours rendered
 * fine inside Salt's ComboBox and then threw the moment it looked for its provider — which is
 * exactly what happened to `searchable-select.tsx`.
 *
 * Salt exports `useListControlContext` but NOT the context object itself, so the bridge has to
 * be a hook call rather than a shared provider. Both are called unconditionally, because hooks
 * cannot be called in a branch; ours wins when present.
 *
 * The vendor's context has a safe default — a no-op `register`, `openState: false` — so an
 * Option outside BOTH renders and does nothing rather than throwing. That is the vendor's
 * behaviour and it is kept: an orphaned option is a caller mistake that shows up immediately as
 * "clicking does nothing", and turning it into a crash would take a page down over one stray
 * element.
 */
export function useListControlContext<Item = string>(): ListControl<Item> {
  const ours = React.useContext(ListControlContext);
  const vendors = useVendorListControlContext();
  return (ours ?? vendors) as unknown as ListControl<Item>;
}
